import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { DesktopOnlyNotice, ErrorNote, Sheet, Spinner } from '../components/ui';

interface AdminUser {
  id: string; email: string; fullName: string; role: 'staff' | 'finance' | 'admin';
  employeeCode: string | null; isActive: boolean; isArchived: boolean;
  lastLoginAt: string | null; hasPassword: boolean;
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: '', fullName: '', role: 'staff', employeeCode: '' });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get<{ users: AdminUser[] }>('/admin/users').then((d) => setUsers(d.users)).catch(() => setUsers([]));

  useEffect(() => { void load(); }, []);

  async function invite() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/admin/users', {
        email: form.email,
        fullName: form.fullName,
        role: form.role,
        ...(form.employeeCode ? { employeeCode: form.employeeCode } : {}),
      });
      setInviteOpen(false);
      setForm({ email: '', fullName: '', role: 'staff', employeeCode: '' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that account');
    } finally {
      setBusy(false);
    }
  }

  async function action(id: string, path: string) {
    await api.post(`/admin/users/${id}/${path}`);
    await load();
  }

  if (!users) return <Spinner label="Loading staff" />;

  return (
    <div>
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Staff accounts</h1>
        <button type="button" className="btn-primary" onClick={() => setInviteOpen(true)}>Invite someone</button>
      </header>
      <DesktopOnlyNotice />
      <ErrorNote>{error}</ErrorNote>

      <div className="card mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Code</th>
              <th className="px-4 py-2 font-medium">State</th>
              <th className="px-4 py-2 font-medium">Last sign-in</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} className={u.isArchived ? 'opacity-50' : ''}>
                <td className="px-4 py-2">{u.fullName}</td>
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2">
                  <select
                    className="rounded border border-slate-300 px-2 py-1 text-sm"
                    value={u.role}
                    onChange={async (e) => {
                      await api.patch(`/admin/users/${u.id}`, { role: e.target.value });
                      await load();
                    }}
                  >
                    <option value="staff">staff</option>
                    <option value="finance">finance</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="px-4 py-2">{u.employeeCode ?? '—'}</td>
                <td className="px-4 py-2">
                  {u.isArchived ? 'archived' : !u.isActive ? 'inactive'
                    : u.hasPassword ? 'active' : 'invited'}
                </td>
                <td className="px-4 py-2">{u.lastLoginAt?.slice(0, 10) ?? 'never'}</td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <button type="button" className="text-brand underline"
                            onClick={() => void action(u.id, 'invite')}>Resend invite</button>
                    <button type="button" className="text-slate-500 underline"
                            onClick={() => void action(u.id, 'revoke-sessions')}>Sign out</button>
                    {!u.isArchived ? (
                      <button type="button" className="text-red-600 underline"
                              onClick={() => void action(u.id, 'archive')}>Archive</button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite someone"
        footer={
          <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void invite()}>
            {busy ? 'Sending…' : 'Create account and email a setup link'}
          </button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            There is no self-registration. The account is created without a password and the person
            chooses their own via a one-time link that expires in 72 hours.
          </p>
          <div>
            <label className="label" htmlFor="iemail">Email</label>
            <input id="iemail" type="email" className="field" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="iname">Full name</label>
            <input id="iname" className="field" value={form.fullName}
                   onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="irole">Role</label>
            <select id="irole" className="field" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="staff">Staff — submits their own reports</option>
              <option value="finance">Finance — reviews and approves everyone's</option>
              <option value="admin">Admin — also manages accounts and settings</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="icode">Employee code (optional)</label>
            <input id="icode" className="field" value={form.employeeCode}
                   onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} />
          </div>
        </div>
      </Sheet>
    </div>
  );
}

export function AdminSettingsPage() {
  const [settings, setSettings] = useState<Record<string, string | boolean | null> | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ settings: Record<string, string | boolean | null> }>('/admin/settings')
      .then((d) => setSettings(d.settings))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load settings'));
  }, []);

  if (!settings) return <Spinner label="Loading settings" />;

  const keys = Object.keys(settings);

  async function save() {
    setError(null);
    setMessage(null);
    try {
      await api.patch('/admin/settings', draft);
      setMessage('Saved.');
      setDraft({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    }
  }

  async function testEmail() {
    setError(null);
    setMessage(null);
    try {
      const res = await api.post<{ sentTo: string }>('/admin/settings/test-email');
      setMessage(`Test email sent to ${res.sentTo}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The test email failed');
    }
  }

  return (
    <div className="max-w-2xl">
      <h1 className="mb-4 text-xl font-semibold">Settings</h1>
      <DesktopOnlyNotice />

      <div className="card space-y-4 p-4">
        {keys.map((key) => {
          const current = settings[key];
          const isSecret = typeof current === 'boolean';
          return (
            <div key={key}>
              <label className="label" htmlFor={key}>{key}</label>
              <input
                id={key}
                className="field"
                type={isSecret ? 'password' : 'text'}
                placeholder={isSecret ? (current ? '•••••••• (set)' : 'not set') : ''}
                value={draft[key] ?? (isSecret ? '' : (current as string | null) ?? '')}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              />
            </div>
          );
        })}

        {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
        <ErrorNote>{error}</ErrorNote>

        <div className="flex gap-2">
          <button type="button" className="btn-primary" onClick={() => void save()}>Save settings</button>
          <button type="button" className="btn-secondary" onClick={() => void testEmail()}>
            Send test email to me
          </button>
        </div>
        <p className="text-xs text-slate-500">
          The test email can only be sent to your own address — there is deliberately no
          recipient field.
        </p>
      </div>
    </div>
  );
}
